import { SVG, Svg } from '@svgdotjs/svg.js';

import { StageConfig } from './stage/stage-config';

export enum StepConfigType
{
  single = 'single',
  multiple = 'multiple',
}

export interface CollageDesignerConfig
{
  readonly productId: string;
  readonly productStoreProductType?: string;
  readonly steps: Array<StepConfig>;
}

export interface CopyPlaceConfig
{
  shortForm?: string;
  part?: string;
  item?: string;
}

export interface CopyRuleConfig
{
  readonly from: CopyPlaceConfig;
  readonly to: CopyPlaceConfig;
}

export interface StepConfig
{
  readonly title: string;
  readonly type: StepConfigType;
  readonly parts: Array<PartConfig>;
  readonly additionalInfoItems: Array<AdditionalInfoItemConfig>;
  readonly textPlaceholdersSettings: TextPlaceholdersSettings;
  readonly imagePlaceholdersSettings: ImagePlaceholdersSettings;
  readonly imageCropperSettings: ImageCropperSettings;
  readonly templatesSettings: TemplatesSettings;
}

export interface PartConfig
{
  readonly id: string;
  readonly editingView: EditingViewConfig;
  readonly templateGroups: Array<TemplateGroupConfig>;
  readonly width?: number;
  readonly height?: number;
  readonly measuredWidth?: number;
  readonly measuredHeight?: number;
  readonly copyRules: Array<CopyRuleConfig>;
  readonly previewType?: string;
  structureUrl?: string;
}

export interface PartInfo {
  config: PartConfig;
  doc: Svg;
}

export interface OverlaysLayerConfig
{
  images?: ReadonlyArray<OverlaysLayerEntryConfig>;
  inlineSVGs?: ReadonlyArray<OverlaysLayerEntryConfig>;
  readonly editableArea?: EditableArea;
}

export interface OverlaysLayerEntryConfig
{
  readonly location: string;
  readonly conditions: Array<ConditionConfig>;
}

export interface EditableArea
{
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EditingViewConfig
{
  readonly type: string;
  readonly overlays: OverlaysLayerConfig;
  readonly reference?: string;
}

export interface TemplateGroupConfig
{
  readonly id: string;
  readonly title: string;
  readonly templates: {[templateId: string]: TemplateConfig};
  readonly templatesCount: number;
  readonly conditions: Array<ConditionConfig>;
  readonly previewUrl?: string;
  isDefault?: boolean;
}

export interface ImagePlaceholdersCount {
  portrait: number;
  landscape: number;
}

export interface TemplateLabelConfig {
  readonly id: string;
  readonly value: string;
}

export interface TemplateConfig
{
  readonly id: string;
  readonly title: string;
  readonly stageConfig?: StageConfig;
  readonly description?: string;
  readonly previewUrl?: string;
  readonly tag?: string;
  isDefault?: boolean;
  readonly imagePlaceholders: ImagePlaceholdersCount;
  readonly labels: Array<TemplateLabelConfig>;
}

export interface ConditionConfig
{
  readonly type: string;
  readonly part?: string;
  readonly value?: string;
}

export enum AdditionalInfoItemConfigPosition
{
  designArea = 'design-area',
}

export interface AdditionalInfoItemConfig
{
  readonly position: AdditionalInfoItemConfigPosition;
  readonly label: string;
  readonly icon?: string;
  readonly location?: string;
  autoDisplay: boolean;
}

export interface TextPlaceholdersSettings
{
  readonly showCreateNewButton?: boolean;
}

export interface ImagePlaceholdersSettings
{
  readonly disableImageRating?: boolean;
}

export interface ImageCropperSettings
{
  readonly showEditableAreaMargins?: boolean;
}

export interface TemplatesSettings
{
  readonly showTemplatePreviewFrames?: boolean;
}
